import { BomStructure } from '../../../../models/bom-structure';
import { sortBy } from '../../../../utils/sort-by';
import { normalizeAmount } from '../../../../utils/format-amount';

// One line = one node of an in-progress order's deep-copied item tree.
// The same bomItemId under two parents within one order is two lines —
// this is the groundwork for routing (Syzyf): operations will hang off these lines.
export interface WorkLine {
  lineId: number;            // OrderItem id on the API side
  bomItemId: number;
  itemName: string;
  parentName: string | null;   // null = top-level product of the order
  parentLineId: number | null; // parent line within the same order's tree; null = top-level
  orderId: number;
  orderNumber: string;
  orderName: string;
  deadline: string | null;   // ISO date, day granularity (no shifts — decided); null = order without a planned end
  treePosition: number;      // position within the order's tree, allocation tiebreak
  depth: number;             // level in the order's tree, 0 on a root — the row's indent, nothing else reads it (0108)
  requiredAmount: number;
  doneAmount: number;        // completing reports only — what is finished, whatever column it was reported in
  unit: string;              // the order item's, copied from the BOM at snapshot time — what both amounts are counted in (0069)
  structure: BomStructure;   // frozen on the order item like the unit — shown as a tag (0108); Purchased skips every stage by default (0115)
  operationProgress: OperationProgress[]; // one entry per operation with at least one report; no dictionary order
  skippedOperationTypeIds: number[]; // operations whose latest decision on the line is "Nic do zrobienia" (0048)
  learnedSkippedOperationTypeIds: number[]; // no fact of its own there, and the same part skipped it last time (0047) — or, on a Purchased line, nobody said anything there at all (0115)
  hasDocumentation: boolean;
  primaryPhotoAttachmentId: number | null; // frozen on the order item at creation, else the BOM item's current one (0080)
}

export interface OperationProgress {
  operationTypeId: number;
  doneAmount: number;
  lastReportedAt: string; // ISO timestamp
}

export interface ProgressAllocation {
  lineId: number;
  delta: number;
}

// What a report says besides the amounts — chosen once per Zgłoś action, copied onto every line.
// operationTypeId null + completesItem true is the pre-dictionary report.
export interface ReportKind {
  operationTypeId: number | null;
  completesItem: boolean;
}

const NO_DEADLINE_SORT_KEY = '9999-12-31';

export namespace WorkLine {
  // Normalized (0111): 55.8 − 18.6 − 37.2 is 0, not a 1e-15 the row would read as still open
  export function remaining(line: WorkLine): number {
    return Math.max(0, normalizeAmount(line.requiredAmount - line.doneAmount));
  }

  export function doneAt(line: WorkLine, operationTypeId: number): number {
    return line.operationProgress.find(p => p.operationTypeId === operationTypeId)?.doneAmount ?? 0;
  }

  export function remainingAt(line: WorkLine, operationTypeId: number): number {
    return Math.max(0, normalizeAmount(line.requiredAmount - doneAt(line, operationTypeId)));
  }

  // A skip holds only while nothing is reported there — a report is the newer fact (0048).
  // The line's own decision or one learned from the part (0047): the stage does not apply either way
  export function skips(line: WorkLine, operationTypeId: number): boolean {
    return doneAt(line, operationTypeId) === 0
      && (line.skippedOperationTypeIds.includes(operationTypeId) || line.learnedSkippedOperationTypeIds.includes(operationTypeId));
  }

  // Nobody decided on this line — it skips because the same part did last time
  export function skipIsLearned(line: WorkLine, operationTypeId: number): boolean {
    return skips(line, operationTypeId) && !line.skippedOperationTypeIds.includes(operationTypeId);
  }

  // What a report of this kind still has room for on the line: the column's remaining
  // for an operation report, the completion remaining for an untagged one.
  export function remainingFor(line: WorkLine, kind: ReportKind): number {
    return kind.operationTypeId == null ? remaining(line) : remainingAt(line, kind.operationTypeId);
  }

  // Deadline-first, then tree order within an order — predictable, matches the table sort.
  // Wg etapów passes what waits at the stage per line: those amounts fill first and only the
  // rest spills in the usual order — otherwise a report of what arrived could land on a line
  // whose pieces are still upstream.
  export function allocate(
    lines: WorkLine[],
    amount: number,
    kind: ReportKind,
    waitingByLineId?: Map<number, number>
  ): ProgressAllocation[] {
    const open = sortBy(
      lines.filter(line => remainingFor(line, kind) > 0),
      line => `${line.deadline ?? NO_DEADLINE_SORT_KEY}|${String(line.treePosition).padStart(6, '0')}`
    );

    const deltaByLineId = new Map<number, number>();
    let left = amount;

    const fill = (room: (line: WorkLine) => number) => {
      for (const line of open) {
        if (left <= 0) {
          break;
        }

        const taken = deltaByLineId.get(line.lineId) ?? 0;
        const delta = Math.min(left, room(line) - taken);

        if (delta > 0) {
          deltaByLineId.set(line.lineId, taken + delta);
          left = normalizeAmount(left - delta);
        }
      }
    };

    if (waitingByLineId) {
      fill(line => Math.min(waitingByLineId.get(line.lineId) ?? 0, remainingFor(line, kind)));
    }

    fill(line => remainingFor(line, kind));

    return open
      .filter(line => deltaByLineId.has(line.lineId))
      .map(line => ({ lineId: line.lineId, delta: deltaByLineId.get(line.lineId)! }));
  }

  export function applyAllocations(
    lines: WorkLine[],
    allocations: ProgressAllocation[],
    sign: 1 | -1,
    kind: ReportKind
  ): WorkLine[] {
    const deltaByLineId = new Map(allocations.map(a => [a.lineId, a.delta]));

    return lines.map(line => {
      const delta = deltaByLineId.get(line.lineId);

      if (delta === undefined) {
        return line;
      }

      return {
        ...line,
        doneAmount: kind.completesItem ? normalizeAmount(line.doneAmount + sign * delta) : line.doneAmount,
        operationProgress: kind.operationTypeId == null
          ? line.operationProgress
          : applyOperationDelta(line.operationProgress, kind.operationTypeId, sign * delta)
      };
    });
  }

  // One stage decision landing on the lines it named: skips = Nic do zrobienia, otherwise Przywróć.
  // The line has a fact of its own now, so what it learned at the stage no longer speaks.
  // Undo passes the lines that had only learned the skip — they go back to that, not to a decision
  export function applySkip(
    lines: WorkLine[],
    lineIds: number[],
    operationTypeId: number,
    skips: boolean,
    relearnLineIds: number[] = []
  ): WorkLine[] {
    return lines.map(line => {
      if (!lineIds.includes(line.lineId)) {
        return line;
      }

      const relearns = relearnLineIds.includes(line.lineId);
      const others = line.skippedOperationTypeIds.filter(id => id !== operationTypeId);
      const learnedOthers = line.learnedSkippedOperationTypeIds.filter(id => id !== operationTypeId);

      return {
        ...line,
        skippedOperationTypeIds: skips && !relearns ? [...others, operationTypeId] : others,
        learnedSkippedOperationTypeIds: relearns ? [...learnedOthers, operationTypeId] : learnedOthers
      };
    });
  }

  // Undo keeps the previous timestamp — the next load brings the server's truth anyway
  function applyOperationDelta(progress: OperationProgress[], operationTypeId: number, delta: number): OperationProgress[] {
    const current = progress.find(p => p.operationTypeId === operationTypeId);
    const others = progress.filter(p => p.operationTypeId !== operationTypeId);
    const doneAmount = normalizeAmount((current?.doneAmount ?? 0) + delta);

    if (doneAmount <= 0) {
      return others;
    }

    return [
      ...others,
      {
        operationTypeId,
        doneAmount,
        lastReportedAt: delta > 0 ? new Date().toISOString() : current!.lastReportedAt
      }
    ];
  }
}
